<template>
  <div class="forced-osc-pro">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$f_drive ≈ f₀ ⇒ Resonance$</div>
    </div>

    <div class="main-layout vertical">
      <div class="simulation-stage dark-stage">
        <div class="hud-panel">
          <div class="hud-item cyan">
            <label>驱动频率 f_drive</label>
            <span class="val">{{ driveF.toFixed(2) }} <small>Hz</small></span>
          </div>
          <div class="hud-item orange">
            <label>稳态振幅 A</label>
            <span class="val">{{ currentAmp.toFixed(1) }} <small>mm</small></span>
          </div>
          <div class="hud-item" :class="isResonant ? 'green' : 'gray'">
             <label>共振状态</label>
             <span class="val">{{ isResonant ? '极端强烈' : '感应微弱' }}</span>
          </div>
        </div>

        <svg width="100%" height="240" viewBox="0 0 450 240" preserveAspectRatio="xMidYMid meet">
          <!-- 共振响应曲线图 (HUD) -->
          <g transform="translate(280, 50)">
             <path :d="resonanceCurvePath" fill="none" stroke="#475569" stroke-width="1.5" stroke-dasharray="4,2" />
             <circle :cx="fToX(driveF)" :cy="ampToY(currentAmp)" r="5" fill="#f43f5e" filter="url(#glow-r)" />
             <text x="75" y="160" text-anchor="middle" fill="#64748b" font-size="9">幅度响应曲线 (A-f)</text>
          </g>

          <!-- 驱动源 (Drive Base) -->
          <rect :x="100 - baseShake" y="180" width="40" height="20" fill="#334155" />
          <text :x="120" y="215" text-anchor="middle" fill="#64748b" font-size="10">驱动源 (驱动力 F·cosωt)</text>

          <!-- 负载球 (Pendulum / Spring-Mass) -->
          <line x1="120" y1="180" :x2="120 + ballPos" :y2="100" stroke="#0ea5e9" stroke-width="2" />
          <circle :cx="120 + ballPos" cy="100" r="12" fill="#fbbf24" filter="url(#glow-r)" />
        </svg>

        <div class="physics-caption">
           规律：当外力驱动频率与物体的固有频率(如1.0Hz)接近时，会导致振幅疯狂增长。
        </div>
      </div>

      <div class="compact-controls">
        <div class="control-card">
           <div class="flex-row items-center gap-6">
              <div class="flex-1">
                 <label class="s-label">调节驱动源频率 f (决定能量输入)：{{ driveF.toFixed(2) }} Hz</label>
                 <el-slider v-model="driveF" :min="0.1" :max="2.0" :step="0.01" size="small"></el-slider>
              </div>
              <div class="btn-group">
                 <el-button type="primary" size="small" @click="resetF">重置为固有频率</el-button>
              </div>
           </div>
        </div>
        
        <!-- 高对比度卡宴红卡片，杜绝关键信息模糊 -->
        <div class="discovery-card-discovery-special" v-latex>
           <div class="icon-wrap"><i class="el-icon-odometer"></i></div>
           <div class="text-content">
             <p><strong>实战物理/建筑抗震：</strong>受迫振动解释了为何大桥在微风中可能崩坍（塔科马海峡大桥）。<strong>解惑：</strong>物体都有自己的“心跳”频率。<strong>观察：</strong>调节滑块。你会发现即使驱动源抖的很猛，只要频率不对，黄球几乎不动。但只要频率精准匹配，黄球就会疯狂振荡。这就是能量“被捕捉”的物理瞬间。</p>
           </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default { props: { knowledgePoint: String },
  name: 'ForcedOscillationViz',
  data() {
    return {
      driveF: 0.5,
      f0: 1.0, // Natural Frequency
      time: 0,
      damping: 0.1
    }
  },
  computed: {
    currentAmp() {
       // Lorentzian response formula: A(w) = F0 / sqrt((w0^2 - w^2)^2 + (2*damping*w*w0)^2)
       const w = this.driveF * 2 * Math.PI;
       const w0 = this.f0 * 2 * Math.PI;
       const denominator = Math.sqrt(Math.pow(w0*w0 - w*w, 2) + Math.pow(2 * this.damping * w * w0, 2));
       return (1500 / denominator) * 2;
    },
    isResonant() { return Math.abs(this.driveF - this.f0) < 0.1; },
    ballPos() { return Math.sin(this.time * this.driveF * 2 * Math.PI) * this.currentAmp; },
    baseShake() { return Math.sin(this.time * this.driveF * 2 * Math.PI) * 5; },
    resonanceCurvePath() {
       let p = "M 0,150 ";
       for(let f=0.1; f<=2.0; f+=0.05) {
          const w = f * 2 * Math.PI;
          const w0 = this.f0 * 2 * Math.PI;
          const amp = (1500 / Math.sqrt(Math.pow(w0*w0 - w*w, 2) + Math.pow(2 * this.damping * w * w0, 2))) * 1.5;
          p += `L ${(f/2)*150},${150 - amp*1.5} `;
       }
       return p;
    }
  },
  mounted() { this.animate(); },
  methods: {
    animate() {
       this.time += 0.02;
       this.animFrame = requestAnimationFrame(this.animate);
    },
    resetF() { this.driveF = this.f0; },
    fToX(f) { return (f/2) * 150; },
    ampToY(amp) { return 150 - amp*1.5; }
  },
  beforeDestroy() {
    if (this.animFrame) cancelAnimationFrame(this.animFrame);
    if (this.timer) clearInterval(this.timer);
  },
}
</script>

<style scoped lang="scss">
@import "./PhysicsCommon.scss";
.forced-osc-pro { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }
</style>
